import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import SignatureCanvas from 'react-signature-canvas';
import { offerApi, authApi } from '../services/api';
import { useScreenshotProtection } from '../hooks/useScreenshotProtection';
import toast from 'react-hot-toast';

const s = {
  page: { minHeight: '100vh', background: '#f0f2f5', padding: '32px 16px' },
  card: { maxWidth: 800, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', overflow: 'hidden', position: 'relative' },
  header: { background: '#1a1a2e', padding: '24px 40px', display: 'flex', alignItems: 'center', gap: 16 },
  logo: { width: 52, height: 52, borderRadius: 8, objectFit: 'contain', background: '#fff', padding: 4 },
  logoPlaceholder: { width: 52, height: 52, borderRadius: 8, background: '#7c83fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 },
  letterBody: { padding: '40px 56px', fontFamily: 'Georgia, serif', color: '#222', lineHeight: 1.8, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', position: 'relative' },
  sigSection: { padding: '28px 40px', borderTop: '2px solid #eee', background: '#fafafa' },
  sigPad: { border: '2px dashed #ccc', borderRadius: 8, width: '100%', height: 160, background: '#fff', cursor: 'crosshair' },
  signBtn: { padding: '13px 36px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginRight: 12 },
  clearBtn: { padding: '13px 20px', background: '#fff', color: '#666', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, cursor: 'pointer' },
  blurOverlay: { position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.95)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff' },
  otpPage: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' },
  otpCard: { background: '#fff', borderRadius: 12, padding: 40, width: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' },
  otpInput: { padding: '12px 16px', border: '1px solid #ddd', borderRadius: 6, fontSize: 22, letterSpacing: 12, width: '100%', textAlign: 'center', boxSizing: 'border-box', marginBottom: 16 },
  btn: { width: '100%', padding: '12px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 8 },
  sectionNav: { display: 'flex', justifyContent: 'center', gap: 8, padding: '16px 40px', borderTop: '1px solid #eee', background: '#fafafa' },
  sectionBtn: (active) => ({ padding: '8px 20px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 20, cursor: 'pointer', background: active ? '#7c83fd' : '#e5e7eb', color: active ? '#fff' : '#555' }),
};

// ── Heavy Dynamic Watermark Component ────────────────────────────────────────
function HeavyWatermark({ candidateName, email, ip, timestamp }) {
  const [offset, setOffset] = useState(0);

  // Dynamic watermark — shifts position every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setOffset(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const watermarkText = `${candidateName} • ${ip} • ${timestamp}`;
  const positions = [
    { top: 8 + offset * 5, left: 2 + offset * 3 },
    { top: 8 + offset * 5, left: 2 + offset * 3 },
  ];

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none', overflow: 'hidden' }}>
      {/* Large diagonal center watermark */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: `translate(-50%, -50%) rotate(-35deg) translateY(${offset * 15}px)`,
        fontSize: 42, fontWeight: 800, color: 'rgba(124,131,253,0.09)',
        whiteSpace: 'nowrap', letterSpacing: 3, userSelect: 'none',
        transition: 'transform 1s ease',
      }}>
        {candidateName}
      </div>

      {/* Repeated diagonal watermarks covering full area */}
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: `${(i * 9 + positions[0].top) % 100}%`,
          left: `${((i * 17 + positions[0].left) % 90) - 5}%`,
          transform: 'rotate(-30deg)',
          fontSize: 9, fontWeight: 700,
          color: `rgba(0,0,0,${0.03 + (i % 3) * 0.01})`,
          whiteSpace: 'nowrap', userSelect: 'none',
          transition: 'top 1.5s ease, left 1.5s ease',
        }}>
          {watermarkText}
        </div>
      ))}

      {/* Diagonal line pattern */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'repeating-linear-gradient(-45deg, transparent, transparent 60px, rgba(124,131,253,0.04) 60px, rgba(124,131,253,0.04) 62px)',
      }} />

      {/* Extra watermarks over salary/terms area (middle of page) */}
      {[35, 45, 55].map(top => (
        <div key={top} style={{
          position: 'absolute', top: `${top + offset * 3}%`, left: '10%', right: '10%',
          textAlign: 'center', transform: 'rotate(-20deg)',
          fontSize: 11, fontWeight: 700, color: 'rgba(124,131,253,0.06)',
          whiteSpace: 'nowrap', userSelect: 'none',
          transition: 'top 1.5s ease',
        }}>
          CONFIDENTIAL • {candidateName} • {email || ip}
        </div>
      ))}
    </div>
  );
}

// ── Secure CTC Annexure (anonymous, one-time view, 10-second countdown) ──────
function SecureCTCAnnexure({ salary, candidateName, ip, onClose }) {
  const [countdown, setCountdown] = useState(10);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (countdown <= 0) {
      setExpired(true);
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  if (expired) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, maxWidth: 420, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>Compensation Details Viewed</h2>
          <p style={{ color: '#666', lineHeight: 1.6, marginBottom: 24 }}>
            This information was available for one-time viewing only.
            Your complete offer details including compensation will be available
            in your signed offer letter after joining confirmation.
          </p>
          <p style={{ color: '#888', fontSize: 12, marginBottom: 24 }}>For any questions, please contact your HR representative.</p>
          <button onClick={onClose} style={{ padding: '12px 32px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            Return to Offer Letter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onContextMenu={e => e.preventDefault()}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 48, maxWidth: 400, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', userSelect: 'none', WebkitUserSelect: 'none' }}>
        {/* Warning */}
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 16px', marginBottom: 24, fontSize: 12, color: '#92400e', fontWeight: 600 }}>
          ⚠️ Secure viewing — this information is visible for {countdown} seconds only
        </div>

        {/* Session-based CTC display — looks temporary, traceable, non-official */}
        <p style={{ color: '#7c83fd', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4 }}>
          Confidential Viewing Session
        </p>
        <p style={{ color: '#aaa', fontSize: 11, marginBottom: 2 }}>
          Viewer ID: {candidateName}
        </p>
        <p style={{ color: '#aaa', fontSize: 11, marginBottom: 16 }}>
          Session Reference: {Math.random().toString(36).substring(2, 8).toUpperCase()}
        </p>

        <p style={{ color: '#888', fontSize: 13, marginBottom: 8 }}>Your Annual Compensation</p>
        <div style={{ fontSize: 36, fontWeight: 800, color: '#1a1a2e', marginBottom: 8, letterSpacing: 1 }}>
          ₹{salary}
        </div>
        <p style={{ color: '#aaa', fontSize: 11, marginBottom: 16 }}>As per the attached offer letter</p>

        {/* Non-official disclaimer — prominent and highlighted */}
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '10px 16px', marginBottom: 24, textAlign: 'center' }}>
          <p style={{ color: '#dc2626', fontSize: 11, fontWeight: 700, margin: 0 }}>
            ⚠️ THIS VIEW IS NOT A VALID OFFER DOCUMENT
          </p>
          <p style={{ color: '#991b1b', fontSize: 10, margin: '4px 0 0', fontStyle: 'italic' }}>
            For official records, refer to your signed offer letter after joining confirmation.
          </p>
        </div>

        {/* Countdown bar */}
        <div style={{ background: '#e5e7eb', borderRadius: 8, height: 6, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{
            background: countdown > 5 ? '#7c83fd' : countdown > 2 ? '#f59e0b' : '#ef4444',
            height: '100%', width: `${(countdown / 10) * 100}%`,
            transition: 'width 1s linear, background 0.3s',
            borderRadius: 8,
          }} />
        </div>
        <p style={{ color: '#999', fontSize: 11 }}>
          Auto-hiding in {countdown}s • One-time view only
        </p>
      </div>
    </div>
  );
}

// ── Section-Based Offer Letter (partial content display) ─────────────────────
function OfferLetter({ offer, onViewCTC, ctcViewed }) {
  const wm = offer.watermark || {};
  const tenantName = wm.tenantName || 'SecureOffer';
  const candidateName = wm.candidateName || '';
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  const [activeSection, setActiveSection] = useState(0);

  let fields = {};
  try { fields = JSON.parse(offer.offerDetails || '{}'); } catch (_) {}

  const position = fields.position || offer.title;
  const startDate = fields.startDate || 'as agreed';
  const location = fields.location || 'our office';
  const empType = fields.employmentType || 'Full-time';
  const department = fields.department || '';
  const reportingTo = fields.reportingTo || '';

  // Show all fields including salary/CTC — screenshots are blocked
  const sensitiveKeys = [];
  const nonSensitiveFields = Object.entries(fields);

  const labelMap = {
    position: 'Position', employmentType: 'Employment Type',
    startDate: 'Start Date', location: 'Location', department: 'Department',
    reportingTo: 'Reporting To', probation: 'Probation Period', benefits: 'Benefits',
  };

  const sections = [
    {
      title: 'Letter',
      content: (
        <>
          <p style={{ color: '#666', fontSize: 13, marginBottom: 24 }}>Date: {today}</p>
          <p style={{ marginBottom: 4 }}>To,</p>
          <p style={{ fontWeight: 700, marginBottom: 24 }}>{candidateName}</p>
          <p style={{ fontWeight: 700, fontSize: 15, borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 20 }}>
            Subject: Offer of Employment — {offer.title}
          </p>
          <p style={{ marginBottom: 16 }}>Dear {candidateName},</p>
          <p style={{ marginBottom: 16 }}>
            We are delighted to extend this offer of employment to you for the position of{' '}
            <strong>{position}</strong> at <strong>{tenantName}</strong>. After careful consideration,
            we believe that your skills and experience make you an excellent fit for our team.
          </p>
        </>
      ),
    },
    {
      title: 'Terms',
      content: (
        <>
          <p style={{ fontWeight: 700, color: '#7c83fd', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
            Terms of Employment
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 13 }}>
            <tbody>
              {nonSensitiveFields.map(([k, v]) => (
                <tr key={k} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 12px', background: '#f8f9fa', fontWeight: 600, width: '35%', color: '#555' }}>
                    {labelMap[k] || k}
                  </td>
                  <td style={{ padding: '8px 12px', color: '#1a1a2e' }}>{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* CTC shown directly in terms table — screenshots are blocked */}
        </>
      ),
    },
    {
      title: 'Details',
      content: (
        <>
          <p style={{ marginBottom: 16 }}>
            You will be based at our <strong>{location}</strong> office and your employment will commence on{' '}
            <strong>{startDate}</strong>. Your compensation details are provided in the secure annexure
            attached to this offer letter.
            {empType && <> This is a <strong>{empType}</strong> position.</>}
            {department && <> You will be part of the <strong>{department}</strong> department.</>}
            {reportingTo && <> You will report to <strong>{reportingTo}</strong>.</>}
          </p>
          <p style={{ marginBottom: 16 }}>
            This offer is contingent upon the successful completion of background verification,
            submission of required documents, and signing of the employment agreement.
          </p>
          <p style={{ marginBottom: 24 }}>
            We look forward to welcoming you to the <strong>{tenantName}</strong> family.
            Please sign this offer letter below to indicate your acceptance.
          </p>
          <p style={{ marginBottom: 4 }}>Warm regards,</p>
          <p style={{ fontWeight: 700, marginBottom: 2 }}>{tenantName}</p>
          <p style={{ color: '#888', fontSize: 13 }}>Human Resources Department</p>
        </>
      ),
    },
  ];

  return (
    <>
      <div style={s.letterBody} onContextMenu={e => e.preventDefault()}>
        {/* Heavy dynamic watermark */}
        <HeavyWatermark
          candidateName={candidateName}
          email={wm.email}
          ip={wm.ipAddress}
          timestamp={wm.timestamp?.split('T')[0] || today}
        />

        {/* Only show active section — partial content display */}
        <div style={{ position: 'relative', zIndex: 5, minHeight: 250 }}>
          {sections[activeSection].content}
        </div>

        {/* Confidential footer */}
        <p style={{ marginTop: 24, fontSize: 9, color: '#ccc', textAlign: 'center', borderTop: '1px solid #f0f0f0', paddingTop: 10, position: 'relative', zIndex: 5 }}>
          Confidential • {candidateName} • {wm.ipAddress} • {wm.timestamp}
        </p>
      </div>

      {/* Section navigation */}
      <div style={s.sectionNav}>
        {sections.map((sec, i) => (
          <button key={i} style={s.sectionBtn(i === activeSection)} onClick={() => setActiveSection(i)}>
            {i + 1}. {sec.title}
          </button>
        ))}
      </div>
    </>
  );
}

// ── Main Page Component ──────────────────────────────────────────────────────
export default function OfferViewPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const sigRef = useRef(null);
  const [step, setStep] = useState('checking');
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [viewBlocked, setViewBlocked] = useState(null);
  const [showCTC, setShowCTC] = useState(false);
  const [ctcViewed, setCTCViewed] = useState(false);
  // viewsRemaining from status check — shown to candidate before they consume a view
  const [viewsRemaining, setViewsRemaining] = useState(null);

  const { isBlurred, warningMessage } = useScreenshotProtection();

  // Check view status before showing OTP form — does NOT consume a view
  useEffect(() => {
    offerApi.viewStatus(token)
      .then(res => {
        if (res.data.canView) {
          setViewsRemaining(res.data.viewsRemaining ?? null);
          setStep('otp');
        } else {
          setViewBlocked(res.data.reason);
        }
      })
      .catch(() => setStep('otp'));
  }, [token]);

  // View API is only called when user explicitly clicks "View Offer" (step === 'view')
  const { data: offer, isLoading, error } = useQuery({
    queryKey: ['offer', token],
    queryFn: () => offerApi.view(token).then(r => r.data),
    enabled: step === 'view',
    staleTime: Infinity,   // never refetch automatically — prevents duplicate view counts
    retry: false,
  });

  const sendOtp = async () => {
    try {
      await authApi.sendOtp(email);
      setOtpSent(true);
      toast.success('OTP sent to your email');
    } catch { toast.error('Failed to send OTP'); }
  };

  const verifyOtp = async () => {
    try {
      const { data } = await authApi.verifyOtp(email, otp);
      // After OTP verified — go to 'ready' step, NOT 'view'
      // This prevents automatic view consumption on OTP verification
      if (data.valid) { setStep('ready'); toast.success('Identity verified'); }
      else toast.error('Invalid OTP');
    } catch { toast.error('OTP verification failed'); }
  };

  const handleSign = async () => {
    if (sigRef.current?.isEmpty()) { toast.error('Please draw your signature below'); return; }
    setSigning(true);
    try {
      const signatureData = sigRef.current.toDataURL('image/png');
      const res = await offerApi.sign({ token, signatureData });
      const signatureId = res.data?.signatureId;
      toast.success('Offer signed successfully');
      if (signatureId) {
        navigate(`/signed/${signatureId}`);
      } else {
        setStep('signed');
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message || 'Signing failed';
      toast.error(`${status ? `[${status}] ` : ''}${msg}`, { duration: 8000 });
    } finally { setSigning(false); }
  };

  const BlurOverlay = () => (
    <div style={s.blurOverlay}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>{warningMessage || 'Content Protected'}</h2>
      <p style={{ color: '#aaa', fontSize: 14 }}>This document is protected. Content is hidden.</p>
    </div>
  );

  // ── Checking Step ─────────────────────────────────────────────────────────
  if (step === 'checking') {
    return <div style={s.page}><div style={{ padding: 40, textAlign: 'center' }}>Checking offer status...</div></div>;
  }

  // ── Ready Step — user must explicitly click to consume a view ─────────────
  if (step === 'ready') {
    return (
      <div style={s.otpPage}>
        <div style={s.otpCard}>
          <div style={{ fontSize: 40, marginBottom: 16, textAlign: 'center' }}>📄</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, textAlign: 'center', marginBottom: 6 }}>Your Offer is Ready</h2>
          <p style={{ color: '#666', textAlign: 'center', marginBottom: 8, fontSize: 14 }}>
            Your identity has been verified. Click below to open your offer letter.
          </p>
          {viewsRemaining !== null && (
            <p style={{ color: '#f59e0b', textAlign: 'center', fontSize: 12, marginBottom: 20, fontWeight: 600 }}>
              ⚠️ You have {viewsRemaining} view{viewsRemaining !== 1 ? 's' : ''} remaining
            </p>
          )}
          <button style={s.btn} onClick={() => setStep('view')}>
            🔓 View Offer Letter
          </button>
          <p style={{ color: '#bbb', fontSize: 11, textAlign: 'center', marginTop: 8 }}>
            Each view is logged for security purposes.
          </p>
        </div>
      </div>
    );
  }

  // ── View Blocked Step ─────────────────────────────────────────────────────
  if (viewBlocked) {
    return (
      <div style={s.page}>
        <div style={{ maxWidth: 500, margin: '80px auto', background: '#fff', borderRadius: 12, padding: 48, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>Offer Unavailable</h2>
          <p style={{ color: '#666', lineHeight: 1.6, marginBottom: 24 }}>{viewBlocked}</p>
          <p style={{ color: '#888', fontSize: 13 }}>
            For security, offer letters have limited viewing access.
            Please contact your HR representative if you need to view this offer again.
          </p>
        </div>
      </div>
    );
  }

  // ── OTP Step ──────────────────────────────────────────────────────────────
  if (step === 'otp') {
    return (
      <div style={s.otpPage}>
        {isBlurred && <BlurOverlay />}
        <div style={s.otpCard}>
          <div style={{ fontSize: 40, marginBottom: 16, textAlign: 'center' }}>🔐</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, textAlign: 'center', marginBottom: 6 }}>Verify Your Identity</h2>
          <p style={{ color: '#666', textAlign: 'center', marginBottom: 24, fontSize: 14 }}>
            Enter your email to receive a one-time verification code.
          </p>
          <input style={{ ...s.otpInput, letterSpacing: 0, fontSize: 14, marginBottom: 12 }}
            type="email" placeholder="your@email.com"
            value={email} onChange={e => setEmail(e.target.value)} />
          {!otpSent ? (
            <button style={s.btn} onClick={sendOtp} disabled={!email}>Send OTP</button>
          ) : (
            <>
              <p style={{ color: '#555', fontSize: 13, marginBottom: 12, textAlign: 'center' }}>
                Enter the 6-digit code sent to <strong>{email}</strong>
              </p>
              <input style={s.otpInput} maxLength={6} placeholder="0  0  0  0  0  0"
                value={otp} onChange={e => setOtp(e.target.value)} />
              <button style={s.btn} onClick={verifyOtp} disabled={otp.length !== 6}>Verify & View Offer</button>
              <button style={{ ...s.btn, background: '#fff', color: '#666', border: '1px solid #ddd' }}
                onClick={() => { setOtpSent(false); setOtp(''); }}>Resend Code</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Signed Step ───────────────────────────────────────────────────────────
  if (step === 'signed') {
    const pdfUrl = `${process.env.REACT_APP_API_URL || '/api'}/offer/signed-pdf/${token}`;
    return (
      <div style={s.page}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '20px 32px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 36 }}>✅</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#166534' }}>Offer Signed Successfully</div>
              <div style={{ color: '#555', fontSize: 13, marginTop: 2 }}>Your digital signature has been recorded.</div>
            </div>
          </div>
          <iframe src={pdfUrl} style={{ width: '100%', height: '85vh', border: 'none', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }} title="Signed Offer Letter" />
        </div>
      </div>
    );
  }

  if (isLoading) return <div style={s.page}><div style={{ padding: 40, textAlign: 'center' }}>Loading offer...</div></div>;
  if (error) {
    const msg = error.response?.data?.message || 'Offer not found or expired';
    const isViewLimit = msg.toLowerCase().includes('view limit');
    return (
      <div style={s.page}>
        <div style={{ maxWidth: 500, margin: '80px auto', background: '#fff', borderRadius: 12, padding: 48, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>{isViewLimit ? '🔒' : '❌'}</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>
            {isViewLimit ? 'View Limit Reached' : 'Offer Unavailable'}
          </h2>
          <p style={{ color: '#666', lineHeight: 1.6, marginBottom: 24 }}>{msg}</p>
        </div>
      </div>
    );
  }

  const logoSrc = offer?.logoBase64 ? `data:${offer.logoContentType || 'image/png'};base64,${offer.logoBase64}` : null;

  return (
    <div style={s.page}>
      {isBlurred && <BlurOverlay />}

      {/* Secure CTC Annexure — anonymous, one-time, 10-second view */}
      {showCTC && (() => {
        let fields = {};
        try { fields = JSON.parse(offer?.offerDetails || '{}'); } catch (_) {}
        const salary = fields.salary || fields.ctc || fields.compensation || 'Not specified';
        return (
          <SecureCTCAnnexure
            salary={salary}
            candidateName={offer?.watermark?.candidateName || ''}
            ip={offer?.watermark?.ipAddress || ''}
            onClose={() => { setShowCTC(false); setCTCViewed(true); }}
          />
        );
      })()}

      <div style={s.card}>
        {/* Company Header */}
        <div style={s.header}>
          {logoSrc
            ? <img src={logoSrc} alt="logo" style={s.logo} />
            : <div style={s.logoPlaceholder}>🏢</div>
          }
          <div>
            <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>{offer?.watermark?.tenantName || 'SecureOffer'}</div>
            <div style={{ color: '#aaa', fontSize: 12 }}>Official Offer Letter</div>
          </div>
        </div>

        {/* Section-based Offer Letter with Heavy Watermarks — CTC removed */}
        <OfferLetter offer={offer} onViewCTC={() => setShowCTC(true)} ctcViewed={ctcViewed} />

        {/* Signature Section */}
        {!offer?.signed ? (
          <div style={s.sigSection}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>✍️ Digital Signature</div>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
              I have read and understood the offer letter above. Draw your signature below to accept.
            </div>
            <SignatureCanvas ref={sigRef} canvasProps={{ style: s.sigPad }} backgroundColor="white" />
            <div style={{ marginTop: 16 }}>
              <button style={s.signBtn} onClick={handleSign} disabled={signing}>
                {signing ? 'Signing...' : '✅ Sign & Accept Offer'}
              </button>
              <button style={s.clearBtn} onClick={() => sigRef.current?.clear()}>Clear</button>
            </div>
            <p style={{ fontSize: 11, color: '#bbb', marginTop: 12 }}>
              Your signature is cryptographically hashed and a signed PDF is generated and stored securely.
            </p>
          </div>
        ) : (
          <div style={{ ...s.sigSection, background: '#f0fdf4', borderTop: '2px solid #bbf7d0' }}>
            <div style={{ color: '#166534', fontWeight: 600, fontSize: 15 }}>✅ This offer has already been signed.</div>
          </div>
        )}
      </div>
    </div>
  );
}
